import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { DataTable, type Column } from '../components/ui/data-table.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { Input, Select } from '../components/ui/input.js';

type EmailProvider = 'microsoft' | 'google' | 'smtp';
type EmailAccountStatus = 'connected' | 'disconnected' | 'error' | 'pending';

interface EmailAccount {
  id: string;
  provider: EmailProvider;
  display_name: string;
  sender_address: string;
  status: EmailAccountStatus;
  last_send_success_at: string | null;
  last_send_error: string | null;
  created_at: string;
}

interface ListResponse {
  data: EmailAccount[];
}

const STATUS_TONE: Record<EmailAccountStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  connected: 'success',
  disconnected: 'neutral',
  pending: 'warning',
  error: 'danger',
};

const PROVIDER_LABEL: Record<EmailProvider, string> = {
  microsoft: 'Microsoft 365',
  google: 'Google Workspace',
  smtp: 'SMTP',
};

export function EmailAccountsPage(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState<'' | EmailProvider>('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => api.get('/v1/email-accounts') as Promise<ListResponse>,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.data.filter((row) => {
      if (providerFilter && row.provider !== providerFilter) return false;
      if (!s) return true;
      return [row.display_name, row.sender_address].some((v) => v && v.toLowerCase().includes(s));
    });
  }, [data, search, providerFilter]);

  const hasFilter = !!search || !!providerFilter;

  const deleteOne = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/email-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-accounts'] }),
  });

  const columns: Column<EmailAccount>[] = [
    {
      key: 'display_name',
      header: 'Name',
      render: (row) => (
        <Link to={`/email-accounts/${row.id}`} className="font-medium hover:text-accent">
          {row.display_name}
        </Link>
      ),
    },
    {
      key: 'sender',
      header: 'Sender',
      render: (row) => <code className="text-xs">{row.sender_address}</code>,
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (row) => PROVIDER_LABEL[row.provider],
      width: '160px',
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status}</Badge>,
      width: '110px',
    },
    {
      key: 'last_send',
      header: 'Last send',
      render: (row) =>
        row.last_send_success_at
          ? new Date(row.last_send_success_at).toLocaleDateString()
          : <span className="text-ink-muted">never</span>,
      width: '120px',
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      className: 'text-right',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (window.confirm(`Delete email account "${row.display_name}"?`)) {
              deleteOne.mutate(row.id);
            }
          }}
          disabled={deleteOne.isPending}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Email accounts</h1>
          <p className="text-sm text-ink-muted">
            BYO email integration. We send through your existing Microsoft 365 / Google
            Workspace / SMTP infrastructure — IP reputation stays under your control.
          </p>
        </div>
        <Link to="/email-accounts/new">
          <Button>Add account</Button>
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All accounts</CardTitle>
          {data && (
            <span className="text-xs text-ink-muted">
              {hasFilter ? `${filtered.length} of ${data.data.length}` : data.data.length}
            </span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {data && (
            <div className="flex gap-2 px-4 py-3 border-b border-border">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, sender address…"
                className="flex-1"
              />
              <Select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value as '' | EmailProvider)}
                className="w-44"
              >
                <option value="">All providers</option>
                <option value="smtp">SMTP</option>
                <option value="microsoft">Microsoft 365</option>
                <option value="google">Google Workspace</option>
              </Select>
              {hasFilter && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setProviderFilter(''); }}
                  className="text-sm text-ink-muted hover:text-ink px-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {isLoading && <p className="p-4 text-sm text-ink-muted">Loading…</p>}
          {error && <ErrorBanner error={error} />}
          {data && (
            <DataTable
              data={filtered}
              columns={columns}
              rowKey={(r) => r.id}
              emptyState={
                hasFilter ? (
                  <EmptyState
                    title="No matches"
                    description="No email accounts match the current filters."
                  />
                ) : (
                  <EmptyState
                    title="No email accounts yet"
                    description="Connect a Microsoft 365, Google Workspace, or generic SMTP account to send emails."
                  />
                )
              }
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="m-4 rounded border border-danger bg-surface p-3 text-sm">
      <p className="font-medium text-danger">Failed to load</p>
      <p className="mt-1 text-ink-muted">
        {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
      </p>
    </div>
  );
}
